import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiArrowLeft, FiEdit3, FiEye } = FiIcons;

const CollaborationRoom = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [isJoining, setIsJoining] = useState(true);

  useEffect(() => {
    // Load session data
    const data = localStorage.getItem(`session_${sessionId}`);
    if (data) {
      setSessionData(JSON.parse(data));
      setIsJoining(false);
    } else {
      // Session not found
      setTimeout(() => navigate('/'), 3000);
    }
  }, [sessionId, navigate]);

  const handleJoinSession = () => {
    navigate(`/wizard/${sessionData.type}?session=${sessionId}`);
  };

  if (isJoining) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading collaboration session...</p>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Session Not Found</h2>
          <p className="text-slate-600 mb-6">This collaboration session doesn't exist or has expired.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiUsers} className="text-3xl text-blue-600" />
          </div>
          
          <h1 className="text-4xl font-bold text-slate-800 mb-4">
            Join Collaboration Session
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            You've been invited to collaborate on a {sessionData.type} agreement
          </p>

          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="text-left">
                <h3 className="font-semibold text-slate-800 mb-2">Agreement Type</h3>
                <p className="text-slate-600 capitalize">{sessionData.type} Agreement</p>
              </div>
              
              <div className="text-left">
                <h3 className="font-semibold text-slate-800 mb-2">Created</h3>
                <p className="text-slate-600">
                  {new Date(sessionData.createdAt).toLocaleDateString()}
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="font-semibold text-slate-800 mb-2">Progress</h3>
                <p className="text-slate-600">
                  {Object.keys(sessionData.answers).length} questions answered
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="font-semibold text-slate-800 mb-2">Status</h3>
                <p className="text-emerald-600 font-medium">Active</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleJoinSession}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transform hover:scale-105 transition-all duration-200"
              >
                <SafeIcon icon={FiEdit3} />
                Join & Edit
              </button>
              
              <button
                onClick={() => navigate(`/preview/${sessionId}`)}
                className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all duration-200"
              >
                <SafeIcon icon={FiEye} />
                View Only
              </button>
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors mt-8 mx-auto"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Home
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default CollaborationRoom;